import { Sync } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { Log, PMButton } from "@pimo/pimo-components";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";

import { formatDateTime } from "../../utils/format-date";

type LogsPageProps = {
  logs: Log[];
  isLoading: boolean;
  language: SupportedLanguage;
  itemName?: string;
};

type LogsPageEventNames = "overlay:load-more-logs";

const resolveUpdatedFieldName = (
  updatedFieldName: string | null | undefined,
  itemName: string,
  language: SupportedLanguage
): string => {
  if (!updatedFieldName) return "";
  if (updatedFieldName.startsWith("basicItem:")) {
    const rawFieldName = updatedFieldName.slice("basicItem:".length);
    return `${itemName}: ${
      getLocalizedMessage(language, rawFieldName) || rawFieldName
    }`;
  }
  return getLocalizedMessage(language, updatedFieldName) || updatedFieldName;
};

export const LogsPage: PimoReactComponent<
  LogsPageProps,
  LogsPageEventNames
> = ({ logs, isLoading, fireEvent, language, itemName = "Item" }) => {
  const handleLoadMore = () => {
    if (!isLoading) {
      fireEvent?.("overlay:load-more-logs");
    }
  };

  return (
    <Box sx={{ padding: "16px 16px 0 16px" }}>
      <List sx={{ padding: 0 }}>
        {logs?.map((entry, index) => (
          <ListItem
            key={`${entry.createdAt}-${index}`}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignContent: "center",
              gap: 2,
              width: "100%",
              padding: "8px 0",
            }}
          >
            <Accordion
              sx={{
                boxShadow: "none",
                width: "100%",
                border: `1px solid ${grey[200]}`,
                borderRadius: "8px",
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  margin: "8px 0",
                },
              }}
            >
              <AccordionSummary
                sx={{
                  padding: "12px 16px",
                  minHeight: "auto",
                  "&.Mui-expanded": {
                    minHeight: "auto",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "0",
                    "&.Mui-expanded": {
                      margin: "0",
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <Sync sx={{ color: "#2196F3", fontSize: "20px" }} />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    {(!entry.action || entry.action === "edit") && (
                      <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                        <Typography component="span" sx={{ fontWeight: 600 }}>
                          {entry.initiator}
                        </Typography>{" "}
                        {getLocalizedMessage(language, "logs.updated") ||
                          "updated"}{" "}
                        {entry.objectName ? `${entry.objectName}` : ""}
                        <Typography
                          component="span"
                          sx={{ color: "#2196F3", fontWeight: 500 }}
                        >
                          {" "}
                          -{" "}
                          {resolveUpdatedFieldName(
                            entry.updatedFieldName,
                            itemName,
                            language
                          )}
                        </Typography>
                      </Typography>
                    )}
                    {entry.action === "create" && (
                      <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                        <Typography component="span" sx={{ fontWeight: 600 }}>
                          {entry.initiator}
                        </Typography>{" "}
                        {getLocalizedMessage(language, "logs.createdIn") ||
                          "created in"}{" "}
                        {entry.objectName ? `${entry.objectName}` : ""}
                        <Typography
                          component="span"
                          sx={{ color: "#2196F3", fontWeight: 500 }}
                        >
                          {" "}
                          -{" "}
                          {resolveUpdatedFieldName(
                            entry.updatedFieldName,
                            itemName,
                            language
                          )}
                        </Typography>
                      </Typography>
                    )}
                    {entry.action === "delete" && (
                      <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                        <Typography component="span" sx={{ fontWeight: 600 }}>
                          {entry.initiator}
                        </Typography>{" "}
                        {getLocalizedMessage(language, "logs.deletedFrom") ||
                          "deleted from"}{" "}
                        {entry.objectName ? `${entry.objectName}` : ""}
                        <Typography
                          component="span"
                          sx={{ color: "#2196F3", fontWeight: 500 }}
                        >
                          {" "}
                          -{" "}
                          {resolveUpdatedFieldName(
                            entry.updatedFieldName,
                            itemName,
                            language
                          )}
                        </Typography>
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {formatDateTime(entry.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              {(entry.previousValue || entry.newValue) && (
                <AccordionDetails sx={{ padding: "0 16px 16px 16px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      justifyContent: "space-between",
                      gap: 2,
                      marginLeft: "36px",
                    }}
                  >
                    {[entry.previousValue, entry.newValue].map(
                      (value, index) => (
                        <Box
                          key={`${value}-${index}`}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "48%",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              marginBottom: "8px",
                              color: grey[700],
                            }}
                          >
                            {index === 0
                              ? getLocalizedMessage(
                                  language,
                                  "logs.previousValue"
                                ) || "Previous Value"
                              : getLocalizedMessage(
                                  language,
                                  "logs.newValue"
                                ) || "New Value"}
                          </Typography>
                          <Typography
                            sx={{
                              border: "1px solid",
                              borderColor: grey[200],
                              borderRadius: "6px",
                              padding: "12px",
                              overflow: "hidden",
                              wordBreak: "break-word",
                              backgroundColor: grey[50],
                              fontSize: "13px",
                              lineHeight: 1.4,
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      )
                    )}
                  </Box>
                </AccordionDetails>
              )}
            </Accordion>
          </ListItem>
        ))}
      </List>
      <Box display="flex" flexDirection="column" alignItems="center" p={2}>
        {!isLoading && logs.length > 0 && (
          <PMButton
            variant="outlined"
            size="medium"
            onClick={handleLoadMore}
            text={
              getLocalizedMessage(language, "logs.loadMore") || "Load More Logs"
            }
          />
        )}
        {isLoading && <CircularProgress sx={{ color: "#2196F3" }} />}
      </Box>
    </Box>
  );
};
